/**
 * Turns a time phrase in a question ("last month", "so far this year", "in the
 * last 30 days") into a concrete date range.
 *
 * Read-path only, like {@link ./fuzzyMatch}. Nothing here can affect a write, so
 * a misread phrase costs a wrong number in a sentence rather than a wrong edit.
 *
 * "Last month" deliberately means the *previous calendar month*, not the last
 * thirty days. A supplier asking what they earned last month is asking an
 * accounting question, and a trailing window would not reconcile against their
 * own books.
 *
 * Every answer that uses a period is expected to name {@link ResolvedPeriod.label}
 * back to the user. That is what makes the fallback safe: when a phrase is not
 * recognised the answer still says which window it actually covered, so an
 * unrecognised "since we opened" reads as "over the last 12 months" rather than
 * silently pretending to be all-time.
 */

import moment from 'moment';
import { containsFuzzyPhrase, tokenize } from './fuzzyMatch';

export interface ResolvedPeriod {
  id: string;
  /**
   * How the period is named back to the user. Phrased to slot straight after a
   * verb — "you were paid out $X {label}." — so answers do not have to special
   * case prepositions.
   */
  label: string;
  /** Inclusive start as `YYYY-MM-DD`. */
  startDate: string;
  /** Inclusive end as `YYYY-MM-DD`. */
  endDate: string;
  /** False when nothing in the message named a period and the default was used. */
  explicit: boolean;
}

const ISO_DAY = 'YYYY-MM-DD';

interface PeriodDefinition {
  id: string;
  label: string;
  /** Phrases that select this period. Matched longest-first. */
  phrases: string[];
  range: () => { start: moment.Moment; end: moment.Moment };
}

const PERIOD_DEFINITIONS: PeriodDefinition[] = [
  {
    id: 'today',
    label: 'today',
    phrases: ['today', 'so far today'],
    range: () => ({ start: moment().startOf('day'), end: moment().endOf('day') }),
  },
  {
    id: 'yesterday',
    label: 'yesterday',
    phrases: ['yesterday'],
    range: () => ({
      start: moment().subtract(1, 'day').startOf('day'),
      end: moment().subtract(1, 'day').endOf('day'),
    }),
  },
  {
    id: 'thisWeek',
    label: 'so far this week',
    phrases: ['this week', 'so far this week', 'week to date'],
    range: () => ({ start: moment().startOf('week'), end: moment().endOf('day') }),
  },
  {
    id: 'lastWeek',
    label: 'last week',
    phrases: ['last week', 'previous week'],
    range: () => ({
      start: moment().subtract(1, 'week').startOf('week'),
      end: moment().subtract(1, 'week').endOf('week'),
    }),
  },
  {
    id: 'thisMonth',
    label: 'so far this month',
    phrases: ['this month', 'so far this month', 'month to date'],
    range: () => ({ start: moment().startOf('month'), end: moment().endOf('day') }),
  },
  {
    id: 'lastMonth',
    label: 'last month',
    phrases: ['last month', 'previous month'],
    range: () => ({
      start: moment().subtract(1, 'month').startOf('month'),
      end: moment().subtract(1, 'month').endOf('month'),
    }),
  },
  {
    id: 'thisQuarter',
    label: 'so far this quarter',
    phrases: ['this quarter', 'quarter to date'],
    range: () => ({ start: moment().startOf('quarter'), end: moment().endOf('day') }),
  },
  {
    id: 'lastQuarter',
    label: 'last quarter',
    phrases: ['last quarter', 'previous quarter'],
    range: () => ({
      start: moment().subtract(1, 'quarter').startOf('quarter'),
      end: moment().subtract(1, 'quarter').endOf('quarter'),
    }),
  },
  {
    id: 'thisYear',
    label: 'so far this year',
    phrases: ['this year', 'year to date', 'ytd'],
    range: () => ({ start: moment().startOf('year'), end: moment().endOf('day') }),
  },
  {
    id: 'lastYear',
    label: 'last year',
    phrases: ['last year', 'previous year'],
    range: () => ({
      start: moment().subtract(1, 'year').startOf('year'),
      end: moment().subtract(1, 'year').endOf('year'),
    }),
  },
];

/**
 * Used when the question names no period.
 *
 * Matches the default range of the payouts report, so the assistant and the
 * report agree when a supplier cross-checks one against the other.
 */
const buildDefaultPeriod = (): ResolvedPeriod => ({
  id: 'trailingYear',
  label: 'over the last 12 months',
  startDate: moment().subtract(1, 'year').format(ISO_DAY),
  endDate: moment().endOf('day').format(ISO_DAY),
  explicit: false,
});

const NUMBER_WORDS: Record<string, number> = {
  one: 1,
  two: 2,
  three: 3,
  four: 4,
  five: 5,
  six: 6,
  seven: 7,
  eight: 8,
  nine: 9,
  ten: 10,
  eleven: 11,
  twelve: 12,
};

type TrailingUnit = 'day' | 'week' | 'month' | 'year';

const TRAILING_IDS: Record<TrailingUnit, string> = {
  day: 'lastNDays',
  week: 'lastNWeeks',
  month: 'lastNMonths',
  year: 'lastNYears',
};

/** "last 30 days", "past two weeks", "previous 6 months", "the last couple of weeks". */
const TRAILING_COUNT = new RegExp(
  `\\b(?:last|past|previous)\\s+(\\d{1,4}|couple(?:\\s+of)?|few|${Object.keys(NUMBER_WORDS).join('|')})` +
    '\\s+(day|week|month|year)s?\\b',
  'i',
);

const countOf = (word: string): number => {
  const lower = word.toLowerCase();
  if (lower.startsWith('couple')) return 2;
  if (lower === 'few') return 3;
  return NUMBER_WORDS[lower] ?? Number(word);
};

/** "In the past month": one of the unit, counted back from today. */
const PAST_UNIT = /\bpast\s+(day|week|month|year)\b/i;

const trailing = (count: number, unit: TrailingUnit, label: string): ResolvedPeriod => ({
  id: TRAILING_IDS[unit],
  label,
  startDate: moment().subtract(count, unit).startOf('day').format(ISO_DAY),
  endDate: moment().endOf('day').format(ISO_DAY),
  explicit: true,
});

/**
 * A window counted back from today. "The last 2 months" is a rolling window
 * even though "last month" on its own is the previous calendar month: a count
 * only makes sense counted from now. "The past month" is rolling too, where
 * the typo-tolerant phrases below would otherwise read "past" as "last".
 */
const detectTrailingCount = (message: string): ResolvedPeriod | null => {
  const match = message.match(TRAILING_COUNT);
  if (match) {
    const count = countOf(match[1]);
    if (!Number.isFinite(count) || count <= 0) return null;
    const unit = match[2].toLowerCase() as TrailingUnit;
    return trailing(count, unit, `in the last ${count} ${unit}${count === 1 ? '' : 's'}`);
  }
  const past = message.match(PAST_UNIT);
  if (!past) return null;
  const unit = past[1].toLowerCase() as TrailingUnit;
  return trailing(1, unit, `in the past ${unit}`);
};

const FULL_MONTHS = moment.months().map((name) => name.toLowerCase());

const MONTH_INDEX = new Map<string, number>([
  ...FULL_MONTHS.map((name, index) => [name, index] as const),
  ...moment.monthsShort().map((name, index) => [name.toLowerCase(), index] as const),
  ['sept', 8],
]);

/** Words that tie a month name to the question: "in August", "since Feb". */
const MONTH_LEAD =
  'in|on|for|during|of|from|since|through|thru|throughout|over|until|to|and|between|last|this';

/**
 * A month name, the word before it, and an optional day, end day and year:
 * "in August", "since Feb 2025", "on March 3rd", "Aug 1-15", "May, 2025".
 */
const MONTH_MENTION = new RegExp(
  `(?:\\b(${MONTH_LEAD})\\s+)?\\b(${[...MONTH_INDEX.keys()].join('|')})\\b\\.?` +
    '(?:\\s+(\\d{1,2})(?:st|nd|rd|th)?\\b(?:\\s*(?:-|\\u2013|to|through|thru|until)\\s*(\\d{1,2})(?:st|nd|rd|th)?\\b)?)?' +
    '(?:\\s*,?\\s*(?:of\\s+)?(\\d{4})\\b)?',
  'gi',
);

/**
 * Capitalised words that can follow a capitalised month without making it a
 * name: what the month is asked about and joining words, as typed in Title
 * Case ("August Revenue", "March Invoices", "August This Year").
 */
const TITLE_CASE_WORDS = new Set([
  'and', 'to', 'through', 'thru', 'until', 'or', 'vs', 'versus', 'of', 'in', 'for', 'from', 'so', 'this', 'last',
  'revenue', 'invoice', 'booking', 'payout', 'payment', 'check', 'check-in', 'checkin', 'check-out', 'checkout',
  'interchange', 'inventory', 'sale', 'earning', 'income', 'fee', 'charge', 'refund', 'report', 'total',
  'activity', 'statement', 'billing', 'bill', 'usage', 'transaction', 'deposit', 'number', 'summary', 'tax',
]);

const isTitleCaseWord = (word: string): boolean => {
  const lower = word.toLowerCase();
  return [lower, lower.replace(/s$/, ''), lower.replace(/es$/, '')].some((form) => TITLE_CASE_WORDS.has(form));
};

/**
 * "Jan's Trucking", "Jan Trucking", "June Smith", "August Logistics". A
 * possessive only marks an abbreviation as a name: "June's revenue" is June.
 */
const looksLikeName = (name: string, rest: string, abbreviated: boolean): boolean => {
  if (abbreviated && /^['\u2019]s\b/i.test(rest)) return true;
  if (!/^[A-Z]/.test(name)) return false;
  const next = rest.match(/^\s+([A-Z][a-z][\w'\u2019-]*)/);
  return next !== null && !isTitleCaseWord(next[1]);
};

/**
 * Leads that do not tie a lone abbreviation, or "May", to a date: "Bob and
 * Jan", "send it to Jan", "count on May". "On" goes with a day ("on May 5th").
 */
const WEAK_LEADS = new Set(['and', 'to', 'on']);

interface MonthMention {
  month: number;
  day: number | null;
  endDay: number | null;
  year: number | null;
  lead: string | null;
  /** Whether the mention is a date on its own, rather than only as part of a range. */
  trusted: boolean;
  /** Where the month name starts, after any lead word. */
  start: number;
  end: number;
}

/**
 * Every month name in a message that could be a date.
 *
 * A full month name is trusted on its own ("August revenue"), except "May",
 * which is far more often the verb. Abbreviations are short enough to be part
 * of a name or another word ("APR"), so a lone abbreviation needs a day, a
 * year or a word like "in" before it. A capitalised month followed by another
 * capitalised word is a name in either form ("Jan Trucking", "June Smith"),
 * so "what did June Smith pay last month" is about last month.
 */
const monthMentions = (message: string): MonthMention[] => {
  const mentions: MonthMention[] = [];
  for (const match of message.matchAll(MONTH_MENTION)) {
    const [whole, leadWord, name, dayText, endDayText, yearText] = match;
    const lower = name.toLowerCase();
    const month = MONTH_INDEX.get(lower);
    if (month === undefined) continue;
    const at = match.index ?? 0;
    const start = at + (leadWord ? whole.indexOf(name, leadWord.length) : 0);
    const abbreviated = !FULL_MONTHS.includes(lower);
    const dated = dayText !== undefined || yearText !== undefined;
    if (!dated && looksLikeName(name, message.slice(start + name.length), abbreviated)) continue;
    const lead = leadWord?.toLowerCase() ?? null;
    const trusted = dated || (!abbreviated && lower !== 'may') || (lead !== null && !WEAK_LEADS.has(lead));
    mentions.push({
      month,
      day: dayText ? Number(dayText) : null,
      endDay: endDayText ? Number(endDayText) : null,
      year: yearText ? Number(yearText) : null,
      lead,
      trusted,
      start,
      end: at + whole.length,
    });
  }
  return mentions;
};

/**
 * The year of a month named without one. "Last year" or "this year" in the
 * question settles it; otherwise it is the latest such month that has begun,
 * so in March "August" is last August and "February" is last month.
 */
const impliedYear = (month: number, message: string): number => {
  const now = moment();
  if (/\b(?:last|previous)\s+year\b/i.test(message)) return now.year() - 1;
  if (/\bthis\s+year\b/i.test(message)) return now.year();
  return month <= now.month() ? now.year() : now.year() - 1;
};

const yearOf = (mention: MonthMention, message: string): number => {
  if (mention.year !== null) return mention.year;
  const now = moment();
  if (mention.lead === 'this') return now.year();
  // "Last August" is the most recent August that is over, never this month.
  if (mention.lead === 'last') return mention.month < now.month() ? now.year() : now.year() - 1;
  return impliedYear(mention.month, message);
};

/** The first day a mention covers; a day that does not exist falls back to the month. */
const firstDayOf = (mention: MonthMention, year: number): moment.Moment => {
  const day = moment({ year, month: mention.month, day: mention.day ?? 1 });
  return day.isValid() ? day : moment({ year, month: mention.month, day: 1 });
};

const hasValidDay = (mention: MonthMention, year: number): boolean =>
  mention.day !== null && moment({ year, month: mention.month, day: mention.day }).isValid();

const lastDayOf = (mention: MonthMention, year: number): moment.Moment => {
  const day = mention.endDay ?? mention.day;
  const candidate = day === null ? null : moment({ year, month: mention.month, day });
  return candidate?.isValid() ? candidate : moment({ year, month: mention.month, day: 1 }).endOf('month');
};

const nameDate = (date: moment.Moment, withDay: boolean, withYear: boolean): string => {
  if (withDay) return date.format(withYear ? 'MMMM D, YYYY' : 'MMMM D');
  return date.format(withYear ? 'MMMM YYYY' : 'MMMM');
};

const toRange = (
  id: string,
  start: moment.Moment,
  end: moment.Moment,
  startHasDay: boolean,
  endHasDay: boolean,
): ResolvedPeriod => ({
  id,
  label: `from ${nameDate(start, startHasDay, start.year() !== end.year())} to ${nameDate(end, endHasDay, true)}`,
  startDate: start.format(ISO_DAY),
  endDate: end.format(ISO_DAY),
  explicit: true,
});

/** Joins two dates into one range: "June to August", "Jan-Mar", "between May and July". */
const RANGE_JOINER = /^(?:-|\u2013|to|through|thru|until|till|and)$/i;

/** "since August", or "from August to now". */
const TO_NOW = /^\s*(?:-|\u2013|to|through|thru|until|till)\s+(?:now|today|date|the present)\b/i;

const monthRange = (from: MonthMention, to: MonthMention, message: string): ResolvedPeriod | null => {
  const joiner = message.slice(from.end, to.start).trim().toLowerCase();
  if (!RANGE_JOINER.test(joiner)) return null;
  // "June and August" is two months, not the three between them.
  if (joiner === 'and' && from.lead !== 'between') return null;

  let fromYear: number;
  if (from.year !== null || to.year === null) fromYear = yearOf(from, message);
  else fromYear = from.month <= to.month ? to.year : to.year - 1;
  const toYear = to.year ?? (to.month >= from.month ? fromYear : fromYear + 1);

  const start = firstDayOf(from, fromYear);
  const end = lastDayOf(to, toYear);
  if (end.isBefore(start)) return null;
  return toRange('dateRange', start, end, hasValidDay(from, fromYear), hasValidDay(to, toYear));
};

const singleMention = (mention: MonthMention, message: string): ResolvedPeriod => {
  const year = yearOf(mention, message);
  const start = firstDayOf(mention, year);
  const withDay = hasValidDay(mention, year);
  const today = moment().endOf('day');

  const toNow = mention.lead === 'since' || TO_NOW.test(message.slice(mention.end));
  if (toNow && start.isBefore(today)) {
    return {
      id: 'sinceDate',
      label: `since ${nameDate(start, withDay, true)}`,
      startDate: start.format(ISO_DAY),
      endDate: today.format(ISO_DAY),
      explicit: true,
    };
  }

  if (withDay && mention.endDay !== null) {
    const end = lastDayOf(mention, year);
    if (end.isAfter(start)) return toRange('dateRange', start, end, true, true);
  }

  if (withDay) {
    return {
      id: 'namedDay',
      label: `on ${nameDate(start, true, true)}`,
      startDate: start.format(ISO_DAY),
      endDate: start.format(ISO_DAY),
      explicit: true,
    };
  }

  return {
    id: 'namedMonth',
    label: `in ${nameDate(start, false, true)}`,
    startDate: start.format(ISO_DAY),
    endDate: start.clone().endOf('month').format(ISO_DAY),
    explicit: true,
  };
};

/**
 * A named month, day or range of them. Two month names joined by "to" or a
 * dash vouch for each other, so "Jan-Mar" is a range even though a lone "Jan"
 * would not be read as a date.
 */
const detectNamedDates = (message: string): ResolvedPeriod | null => {
  const mentions = monthMentions(message);
  for (let i = 0; i + 1 < mentions.length; i += 1) {
    const range = monthRange(mentions[i], mentions[i + 1], message);
    if (range) return range;
  }
  const first = mentions.find((mention) => mention.trusted);
  return first ? singleMention(first, message) : null;
};

const QUARTER_ORDINALS: Record<string, number> = {
  first: 1,
  '1st': 1,
  second: 2,
  '2nd': 2,
  third: 3,
  '3rd': 3,
  fourth: 4,
  '4th': 4,
};

/** "Q3", "q1 2025", "Q4-2025", "the second quarter of 2025". */
const NAMED_QUARTER = new RegExp(
  `\\b(?:q([1-4])|(${Object.keys(QUARTER_ORDINALS).join('|')})\\s+quarter)\\b` +
    '(?:\\s*(?:,|-|of\\b)?\\s*(\\d{4})\\b)?',
  'i',
);

const detectNamedQuarter = (message: string): ResolvedPeriod | null => {
  const match = message.match(NAMED_QUARTER);
  if (!match) return null;
  const quarter = match[1] ? Number(match[1]) : QUARTER_ORDINALS[match[2].toLowerCase()];
  const firstMonth = (quarter - 1) * 3;
  const year = match[3] ? Number(match[3]) : impliedYear(firstMonth, message);
  const start = moment({ year, month: firstMonth, day: 1 });
  return {
    id: 'namedQuarter',
    label: `in Q${quarter} ${year}`,
    startDate: start.format(ISO_DAY),
    endDate: start.clone().add(2, 'months').endOf('month').format(ISO_DAY),
    explicit: true,
  };
};

const YEAR = '((?:19|20)\\d{2})';

/** "2024 to 2025", "2024-2025", "between 2024 and 2025"; not part of "INV-2024-2025". */
const YEAR_RANGE = new RegExp(
  `(?:^|[^\\w#/$-])(?:between\\s+${YEAR}\\s+and\\s+${YEAR}|${YEAR}\\s*(?:-|\\u2013|to|through|thru|until)\\s*${YEAR})(?![\\w/-])`,
  'i',
);

/**
 * A year tied to the question by the word before it: "in 2025", "since 2024",
 * "my 2025 revenue". A bare number is as likely to be a count or part of a
 * location name, and "in 2025 Main St" is an address.
 */
const NAMED_YEAR = new RegExp(
  `\\b(in|for|during|throughout|over|from|since|of|year|my|our|total)\\s+(?:the\\s+)?(?:year\\s+)?${YEAR}` +
    '(?![\\w/-]|[.,]\\d)',
  'i',
);

/** A capitalised word straight after a number: "2025 Main St". */
const STREET_AFTER = /^\s+[A-Z][a-z]/;

const plausibleYear = (year: number): boolean => year >= 2010 && year <= moment().year() + 1;

const detectNamedYear = (message: string): ResolvedPeriod | null => {
  const range = message.match(YEAR_RANGE);
  if (range) {
    const from = Number(range[1] ?? range[3]);
    const to = Number(range[2] ?? range[4]);
    if (plausibleYear(from) && plausibleYear(to) && from < to) {
      return {
        id: 'dateRange',
        label: `from ${from} to ${to}`,
        startDate: `${from}-01-01`,
        endDate: `${to}-12-31`,
        explicit: true,
      };
    }
  }

  const match = message.match(NAMED_YEAR);
  if (!match || STREET_AFTER.test(message.slice((match.index ?? 0) + match[0].length))) return null;
  const year = Number(match[2]);
  if (!plausibleYear(year)) return null;
  const start = moment({ year, month: 0, day: 1 });
  const toNow = match[1].toLowerCase() === 'since' || TO_NOW.test(message.slice((match.index ?? 0) + match[0].length));
  if (toNow && start.isBefore(moment())) {
    return {
      id: 'sinceDate',
      label: `since ${year}`,
      startDate: start.format(ISO_DAY),
      endDate: moment().endOf('day').format(ISO_DAY),
      explicit: true,
    };
  }
  return {
    id: 'namedYear',
    label: `in ${year}`,
    startDate: start.format(ISO_DAY),
    endDate: start.clone().endOf('year').format(ISO_DAY),
    explicit: true,
  };
};

const toPeriod = (definition: PeriodDefinition): ResolvedPeriod => {
  const { start, end } = definition.range();
  return {
    id: definition.id,
    label: definition.label,
    startDate: start.format(ISO_DAY),
    endDate: end.format(ISO_DAY),
    explicit: true,
  };
};

/**
 * Finds the period a message is asking about, or null when it names none.
 *
 * Phrases are matched typo-tolerantly and longest-first, so "so far this month"
 * is not shortened to "this month" if the two ever diverge.
 */
export const detectPeriod = (message: string): ResolvedPeriod | null => {
  // An explicit count is unambiguous, so it wins over the named periods —
  // otherwise "the last 30 days" would be caught by nothing and fall through,
  // and a phrase like "last 7 days" must not be read as a calendar week.
  const trailing = detectTrailingCount(message);
  if (trailing) return trailing;

  // A named month, quarter or year is more specific than the phrases below, and
  // has to be read first so "August last year" is August, not all of last year.
  const named = detectNamedDates(message) ?? detectNamedQuarter(message) ?? detectNamedYear(message);
  if (named) return named;

  const tokens = tokenize(message);

  let best: PeriodDefinition | null = null;
  let bestLength = 0;

  for (const definition of PERIOD_DEFINITIONS) {
    for (const phrase of definition.phrases) {
      if (phrase.length <= bestLength) continue;
      if (!containsFuzzyPhrase(tokens, tokenize(phrase))) continue;
      best = definition;
      bestLength = phrase.length;
    }
  }

  return best ? toPeriod(best) : null;
};

/** Same as {@link detectPeriod} but always yields a usable range. */
export const resolvePeriod = (message: string): ResolvedPeriod =>
  detectPeriod(message) ?? buildDefaultPeriod();

/**
 * Whether the window a period describes has already closed.
 *
 * Lives here rather than at the call site so that reading a period's raw dates
 * stays confined to this module and `apiDateRange` — those dates are ISO, and
 * handing them to an API that expects MM/DD/YYYY is a bug that tests with
 * mocked request modules cannot see.
 */
export const hasPeriodEnded = (period: Pick<ResolvedPeriod, 'endDate'> | null | undefined): boolean => {
  const end = period?.endDate;
  return Boolean(end) && moment(end).isBefore(moment().startOf('day'));
};
